import * as shared from "@weights/plan-reading";
import type { BlockWire, PlanDefinition, PlanDraft, PlannedExerciseWire, RecordKind } from "@weights/plan-reading";
import { expandDefinition } from "./lines.js";

/**
 * DESIGN §61.2: the reading the Worker keeps with each definition, as `PUT /web/api/plans/:monday`,
 * `POST /web/api/plans/read` and the assistants' `verified` frames carry it. Wire types are kept here
 * until plan-reading exports `DefinitionReading`; they are structural, so its own type fits them.
 */

/** What a set of an item records; `none` is Not logged: the words go to the phone as text. */
export type LogKind = RecordKind | "none";

export const LOG_KINDS: readonly LogKind[] = ["weightReps", "reps", "time", "done", "none"];
export const LOG_LABELS: Record<LogKind, string> = { weightReps: "weight × reps", reps: "reps", time: "time", done: "done", none: "not logged" };

export interface ReadingExercise extends PlannedExerciseWire {
  /** The coach's line it came from, 0-based in the text with the definitions it names written in. */
  line: number;
  /** Its place in an inline round. */
  member?: number;
  logs: LogKind;
  logsSetBy?: "coach";
}

export interface ReadingBlock extends Omit<BlockWire, "exercises"> {
  exercises: ReadingExercise[];
}

export interface DefinitionReading {
  version: number;
  family: "weights" | "run" | "other";
  blocks: ReadingBlock[];
  unread: { line: number; text: string }[];
  /** Client only, never sent: the label and lines it was read from, so a reading of older text is never shown as this one's. */
  readFrom?: string;
}

const KINDS = new Set<string>(LOG_KINDS);

/** The reading a definition carries, when it has the shape of one. */
export function readingOf(def: PlanDefinition | undefined): DefinitionReading | undefined {
  const r = (def as { reading?: unknown } | undefined)?.reading;
  return isReading(r) ? r : undefined;
}

export function isReading(r: unknown): r is DefinitionReading {
  if (!r || typeof r !== "object") return false;
  const x = r as Partial<DefinitionReading>;
  return Array.isArray(x.blocks) && Array.isArray(x.unread) && x.blocks.every((b) => !!b && Array.isArray(b.exercises) && b.exercises.every((e) => !!e && typeof e.line === "number" && KINDS.has(e.logs)));
}

export function readingKey(label: string, lines: readonly string[]): string {
  return label + "\u0000" + lines.join("\n");
}

export function stamped(reading: DefinitionReading, label: string, lines: readonly string[]): DefinitionReading {
  return { ...reading, readFrom: readingKey(label, lines) };
}

/** The definition's reading when it was read from the text it has now (§61.10); else none, and the browser's preview stands. */
export function freshReading(def: PlanDefinition | undefined): DefinitionReading | null {
  const r = readingOf(def);
  return r && def && r.readFrom === readingKey(def.label, def.lines) ? r : null;
}

/** Every definition's reading marked as read from its own text: a draft as the server answered it. */
export function stampDraft(draft: PlanDraft): PlanDraft {
  let changed = false;
  const sessions = draft.sessions.map((def) => {
    const r = readingOf(def);
    if (!r) return def;
    changed = true;
    return { ...def, reading: stamped(r, def.label, def.lines) } as PlanDefinition;
  });
  return changed ? { ...draft, sessions } : draft;
}

/** The draft as the server takes it: readings without the client's mark. */
export function forServer(draft: PlanDraft): PlanDraft {
  if (!draft.sessions.some((d) => readingOf(d)?.readFrom !== undefined)) return draft;
  return {
    ...draft,
    sessions: draft.sessions.map((def) => {
      const r = readingOf(def);
      if (!r || r.readFrom === undefined) return def;
      const { readFrom: _, ...rest } = r;
      return { ...def, reading: rest } as PlanDefinition;
    }),
  };
}

const sameJSON = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * A save's answer taken in (§61.10): each definition still as it was sent, its text and its reading,
 * takes the reading the Worker made of it, in what is shown and in what the server is known to hold.
 */
export function takeReadings(sent: PlanDraft, answer: PlanDraft | undefined, current: PlanDraft): { draft: PlanDraft; saved: PlanDraft } {
  if (!answer || !Array.isArray(answer.sessions)) return { draft: current, saved: sent };
  const theirs = new Map(answer.sessions.map((d) => [d.label, d]));
  const swap = (draft: PlanDraft, also: (def: PlanDefinition) => boolean): PlanDraft => {
    let changed = false;
    const sessions = draft.sessions.map((def) => {
      const made = theirs.get(def.label);
      const r = readingOf(made);
      if (!made || !r || !sameJSON(made.lines, def.lines) || !also(def)) return def;
      changed = true;
      return { ...def, reading: stamped(r, def.label, def.lines) } as PlanDefinition;
    });
    return changed ? { ...draft, sessions } : draft;
  };
  const sentBy = new Map(sent.sessions.map((d) => [d.label, d]));
  const saved = swap(sent, () => true);
  const draft = swap(current, (def) => {
    const was = sentBy.get(def.label);
    return !!was && sameJSON(was.lines, def.lines) && sameJSON(readingOf(was), readingOf(def));
  });
  return { draft, saved };
}

// MARK: Lines and items

type ExpandLines = (label: string, lines: readonly string[], definitions?: readonly { label: string; lines: readonly string[] }[]) => { lines: string[]; own: number[] };
// The spread keeps the bundler from resolving a name the package may not export.
const expandLines = ({ ...shared } as Record<string, unknown>).expandLines as ExpandLines | undefined;

/**
 * For each line of the expanded text the reading's `line` counts in (the Worker's `expandLines`), the
 * definition's own line, or null for a line of a definition it names: those items are that one's to pick.
 */
export function ownLineMap(label: string, lines: readonly string[], others: readonly PlanDefinition[]): (number | null)[] {
  if (expandLines) {
    try {
      const x = expandLines(label, lines, others);
      return x.own.map((n, i) => (x.lines[i] === lines[n] ? n : null));
    } catch {
      // Read as the builder expands, below.
    }
  }
  const { origins } = expandDefinition(label, [...lines], others);
  return origins.map((o) => ("own" in o ? o.own : null));
}

/** The items line `n` of his text became, in the reading's order. */
export function itemsOnLine(reading: DefinitionReading, n: number, own: readonly (number | null)[]): ReadingExercise[] {
  return reading.blocks.flatMap((b) => b.exercises.filter((e) => own[e.line] === n));
}

/** The reading with what line `n` (one member of it, when given) logs set by the coach (§53.5, §61.2). */
export function withPick(reading: DefinitionReading, n: number, member: number | undefined, kind: LogKind, own: readonly (number | null)[]): DefinitionReading {
  return {
    ...reading,
    blocks: reading.blocks.map((b) => ({
      ...b,
      exercises: b.exercises.map((e) => (own[e.line] === n && (member === undefined || e.member === member) ? { ...e, logs: kind, logsSetBy: "coach" as const } : e)),
    })),
  };
}

/** The unread lines as his own lines, for the marks: a line of a named definition is the line naming it. */
export function unreadOwnLines(reading: DefinitionReading, own: readonly (number | null)[]): number[] {
  return [...new Set(reading.unread.map((u) => own[u.line]).filter((x): x is number => x !== null && x !== undefined))];
}

/** As PhoneList draws it: each item's kind as the phone's `recordOverride`; Not logged items left out, their words kept. */
export function phonePlan(reading: DefinitionReading): { blocks: BlockWire[]; text: Map<string, string[]> } {
  const text = new Map<string, string[]>();
  const blocks = reading.blocks.map((b) => {
    const kept: PlannedExerciseWire[] = [];
    const words: string[] = [];
    for (const e of b.exercises) {
      if (e.logs === "none") words.push(e.name);
      else {
        const { line: _l, member: _m, logs, logsSetBy: _s, ...wire } = e;
        kept.push({ ...wire, recordOverride: logs });
      }
    }
    if (words.length) text.set(b.id, words);
    return { ...b, isPrep: !!b.isPrep, exercises: kept } as BlockWire;
  });
  return { blocks, text };
}

/** A line the Worker could not read, in one of the week's definitions (§61.9's publish warning). */
export interface UnreadLine {
  label: string;
  /** His own line, 0-based. */
  line: number;
  text: string;
}

/**
 * The unread lines of the definitions the week's cells name: from each one's saved reading, or, where
 * it has none of its text yet, the browser's (`typed`, by label), found in his lines by their text.
 */
export function weekUnread(draft: PlanDraft, used: ReadonlySet<string>, keyOf: (label: string) => string, typed: ReadonlyMap<string, readonly string[]> = new Map()): UnreadLine[] {
  const out: UnreadLine[] = [];
  for (const def of draft.sessions) {
    if (!used.has(keyOf(def.label)) || !def.lines.some((l) => l.trim())) continue;
    const r = freshReading(def);
    if (!r) {
      for (const text of typed.get(def.label) ?? []) out.push({ label: def.label, line: def.lines.findIndex((l) => l.trim() === text.trim()), text: text.trim() });
      continue;
    }
    if (!r.unread.length) continue;
    const own = ownLineMap(def.label, def.lines, draft.sessions);
    for (const u of r.unread) out.push({ label: def.label, line: own[u.line] ?? 0, text: u.text.trim() });
  }
  return out;
}
